import { SvgIconProps } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
import { Labels, SeriesConfig, TimeSeriesValueTuple } from '@chronosphereio/viz-framework-core';
import { BarSeriesOption, LineSeriesOption, ScatterSeriesOption } from 'echarts/charts';
import { DatasetOption } from 'echarts/types/dist/shared';
import { HTMLAttributeAnchorTarget } from 'react';
import { AppMetadataProps } from './app-metadata';
import { QueryMetadata } from './query';

export interface TimeScale {
  startMs: number;
  endMs: number;
  stepMs?: number;
  rangeMs: number;
}

export interface TimeSeriesData {
  timeRange?: AbsoluteTimeRange;
  stepMs?: number;
  series: TimeSeries[];
  metadata?: TimeSeriesMetadata;
}

export type TimeSeriesMetadata = QueryMetadata;

export interface TimeSeries {
  name: string;
  values: TimeSeriesValueTuple[];
  formattedName?: string;
  labels?: Labels;
  config?: SeriesConfig;
}

/*
 * ECharts specific types used for TimeChart dataset support.
 * Each time series returned is mapped to series options using datasetIndex.
 * https://apache.github.io/echarts-handbook/en/concepts/dataset/#how-to-reference-several-datasets
 */
export type TimeChartSeriesMapping = TimeSeriesOption[];

/**
 * TimeSeriesOption extends the default ECharts series options to include a custom `displayName` property.
 */
export type TimeSeriesOption = (LineSeriesOption | BarSeriesOption | ScatterSeriesOption) & {
  /**
   * Used to access series name in the tooltip since we pass id as the name so it is available in mouseover event params.
   * This property is ignored by ECharts but can be used in custom React components like TimeChartTooltip, getSeriesInfo util.
   */
  formattedSeriesName?: string;
  isComparison?: boolean;
  /**
   * Marks the series as a per-query threshold reference line. Ignored by ECharts;
   * used by the tooltip to render a dashed marker distinct from compare mode.
   */
  isQueryThresholdSeries?: boolean;
};

export function isLineSeriesOption(series: TimeSeriesOption): series is LineSeriesOption {
  return (series as LineSeriesOption).type === 'line';
}

export type TimeSeriesDatasetSource = [number, number | null];
export const TIME_SERIES_DATASET_DIMENSIONS = ['time', 'value'] as const;

/**
 * Opinionated version of the ECharts `dataset` that should be used with the time series chart.
 * We can also pass custom series properties here (ex: config, labels, calcs) since ECharts will ignore them.
 * This allows us to build custom UX for the tooltip / legend without duplication or extra
 * processing of query results.
 */
export interface TimeSeriesDataset extends Pick<Required<DatasetOption>, 'name' | 'id'> {
  // Intentionally defining something much more opinionated here because the `source`
  // on `DatasetOption` is very open-ended and supports a bunch of different shapes
  // whereas we want to be prescriptive.
  source: TimeSeriesDatasetSource[];
  dimensions: typeof TIME_SERIES_DATASET_DIMENSIONS;
  queryResultIndex?: number;
  config?: SeriesConfig;
  labels?: Labels;
  /**
   * Display name for the series, including alias and custom formatting.
   * Used in legend labels, overrides, color generation, and tooltip display.
   * Falls back to `name` (raw labels) when not set.
   */
  formattedName?: string;
  compareToSeriesId?: string;
  /**
   * Identifier for the group the time series is associated with. Only set when the "group by"
   * feature is in use.
   */
  groupKey?: string;
  /**
   * Series calculations that are precomputed to optimize performance.
   */
  calcs?: {
    /**
     * Sum of all values for this series across the entire time range.
     * Precomputed in getTimeSeriesValues and used for stack sorting.
     */
    totalValue: number;
  };
  /**
   * Condensed series name with common labels removed (for tooltip display).
   * Only set when common labels are detected across all series.
   */
  condensedName?: string;
  /**
   * Data points surrounded by null values on both sides, indicating isolated/sparse points.
   * Used to determine whether to render individual symbols for the series.
   */
  nonconnectedDataPoints?: TimeSeriesValueTuple[];
}

// We leave out values here and instead store the index of the data in the
// dataset, so we can look it up instead of duplicating a potentially large
// data structure.
export type TimeSeriesWithoutValues = Omit<TimeSeries, 'values'>;

export type SeriesByIdMapValue = TimeSeriesWithoutValues & {
  datasetIndex: number; // https://apache.github.io/echarts-handbook/en/concepts/dataset/#how-to-reference-several-datasets
  mappingIndex: number; // Used to lookup ECharts option properties from seriesMapping in our custom tooltip.
  queryResultsIndex: number; // Used to look up the query the series is associated to
};

export type TimeSeriesWithMapping = TimeSeriesWithoutValues & { mappingIndex: number; queryResultsIndex: number };

export type GetSeriesById = ((id: string) => TimeSeriesWithMapping | undefined) | undefined;

interface BaseSeriesMenuItemOptions {
  title: string;
  icon: React.ElementType<SvgIconProps>;
  description?: string;
}

/**
 * SeriesMenuItemOptions supports either onClick or href.
 */
export type SeriesMenuItemOptions = SeriesMenuItemOptionsHref | SeriesMenuItemOptionsOnClick;

type SeriesMenuItemOptionsOnClick = BaseSeriesMenuItemOptions & {
  onClick: () => void;
  href?: never;
};

type SeriesMenuItemOptionsHref = BaseSeriesMenuItemOptions & {
  href: string;
  target?: HTMLAttributeAnchorTarget;
  onClick?: never;
};

export type TooltipContextMenuCtx = {
  timestamp: number;
  isTooltipPinned: boolean;
  seriesKey: string;
  appMetadata?: AppMetadataProps;
};

export function isHrefSeriesMenuItem(item: SeriesMenuItemOptions): item is SeriesMenuItemOptionsHref {
  return 'href' in item;
}

export function isOnClickSeriesMenuItem(item: SeriesMenuItemOptions): item is SeriesMenuItemOptionsOnClick {
  return 'onClick' in item;
}
